import {
  Building01Icon,
  CalendarDateIcon,
  File02Icon,
  FileCheck02Icon,
  LineChartUp02Icon,
  MagicWand01Icon,
  PackageIcon,
  PlayIcon,
  Mail01Icon,
  Settings01Icon,
  Settings03Icon,
  SidebarMenuItem,
  Users01Icon,
} from "components";
import { Paths, Permissions } from "configs";
import { usePermission } from "hooks";
import { i18n } from "i18next";

interface SidebarMenuRoute extends SidebarMenuItem {
  permissions?: Array<keyof typeof Permissions> | null;
  useSomePermission?: boolean;
  children?: Array<SidebarMenuRoute>;
}

export const getSidebarMenuRoutes = (
  t: i18n["t"],
  canAccess: ReturnType<typeof usePermission>["canAccess"]
): SidebarMenuRoute[] => {
  const list = [
    {
      id: "Company",
      title: t("company"),
      icon: Building01Icon,
      children: [
        {
          id: "company-info",
          title: t("setting"),
          icon: Building01Icon,
          path: Paths.CompanyInfo,
          permissions: ["CompanyDetail"],
        },
        {
          id: "payroll-cyle",
          title: t("payrollCycle"),
          icon: Settings03Icon,
          path: Paths.PayrollCycle,
          permissions: ["PayrollCycleList"],
        },
        {
          id: "title",
          title: t("sideBarTitle"),
          icon: Building01Icon,
          path: Paths.Title,
          permissions: ["CompanyTitleList"],
        },
        {
          id: "work-level",
          title: t("sideBarWorkLevel"),
          icon: Building01Icon,
          path: Paths.WorkLevel,
          permissions: ["PayrollSettingWorkLevelList"],
        },
        {
          id: "kpi-setting",
          title: t("kpiEvaluationProgram"),
          icon: Building01Icon,
          path: Paths.KPISetting,
          permissions: ["PayrollSettingKPISettingList"],
        },

        {
          id: "contract-type",
          title: t("sideBarContractType"),
          icon: Building01Icon,
          path: Paths.ContractType,
          permissions: ["CompanyContractTypeList"],
        },
        {
          id: "department",
          title: t("sideBarDepartment"),
          icon: Building01Icon,
          path: Paths.Department,
          permissions: ["CompanyDepartmentList"],
        },
        {
          id: "branch",
          title: t("sideBarBranch"),
          icon: Building01Icon,
          path: Paths.Branch,
          permissions: ["CompanyBranchList"],
        },
        {
          id: "orgChart",
          title: t("sideBarOrgChart"),
          icon: Building01Icon,
          path: Paths.OrgChart,
          permissions: ["CompanyOrgChartList"],
        },
        {
          id: "roleAndPermission",
          title: t("roleAndPermission"),
          icon: Building01Icon,
          path: Paths.RoleAndPermission,
          useSomePermission: true,
          permissions: ["CompanyGroupEmployeeList", "CompanyRoleList"],
        },
      ],
    },

    {
      id: "Employee",
      title: t("sideBarEmployee"),
      path: Paths.Employee,
      icon: Users01Icon,
      permissions: ["EmployeeList"],
    },
    {
      id: "payrollManagement",
      title: t("sideBarPayrollManagement1"),
      icon: MagicWand01Icon,
      children: [
        {
          id: "TimeAttendance",
          title: t("sideBarTimeAttendance"),
          path: Paths.TimeAttendance,
          icon: CalendarDateIcon,
          permissions: ["TimeAttendanceDataList"],
        },
        {
          id: "run Payroll",
          title: t("sideBarRunPayroll"),
          path: Paths.RunPayroll,
          icon: PlayIcon,
          permissions: ["RunPayrollExecute"],
        },
        {
          id: "payroll",
          title: t("sideBarPayroll1"),
          path: Paths.Payroll,
          icon: FileCheck02Icon,
          permissions: ["PayrollDetail"],
        },
        {
          id: "PAProductivity13thMonth",
          title: t("runCalculate13th"),
          icon: Settings01Icon,
          path: Paths.Report13thMonthSalary,
          permissions: ["PA13thMonthSalary"],
        },
        {
          id: "KPIIndividual",
          title: t("individualKpiBonusTable"),
          icon: Settings01Icon,
          path: Paths.KPIIndividual,
          permissions: ["KpiIndividualsList"],
        },
        {
          id: "KPIDepartment",
          title: t("departmentKpiBonusTable"),
          icon: Settings01Icon,
          path: Paths.KPIDepartment,
          permissions: ["KpiDepartmentsList"],
        },
        {
          id: "adjustment",
          title: t("adjustment"),
          path: Paths.Adjustment,
          icon: FileCheck02Icon,
          permissions: ["AdjustmentList"],
        },
        {
          id: "salary-advance",
          title: t("paymentCenter"),
          path: Paths.SalaryAdvance,
          icon: FileCheck02Icon,
          useSomePermission: true,
          permissions: ["SalaryAdvanceList", "SalaryPaymentList"],
        },
        {
          id: "pit",
          title: t("pitTransaction"),
          path: Paths.PIT,
          icon: FileCheck02Icon,
          useSomePermission: true,
          permissions: ["PITList"],
        },
      ],
    },
    {
      id: "report",
      title: t("report"),
      icon: LineChartUp02Icon,
      path: Paths.Report,
      permissions: [
        "ReportPayrollSummaryList",
        "ReportPayrollReconcileList",
        "ReportSHUIContributionList",
        "ReportOTReportList",
        "ReportAttendanceReportList",
        "ReportLeaveReportList",
        "ReportPITSummaryList",
      ],
      useSomePermission: true,
    },
  ];

  const filterList = list
    .map((item) => {
      if (Array.isArray(item.children) && item.children?.length) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children = (item?.children as any[])?.filter((child) => {
          if (!child.permissions?.length) return true;
          if (child?.useSomePermission) {
            return !!(
              Array.isArray(child?.permissions) &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              child?.permissions?.some((permission: string) =>
                canAccess(permission as never)
              )
            );
          }
          return !!(
            Array.isArray(child?.permissions) &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            child?.permissions?.every((permission: string) =>
              canAccess(permission as never)
            )
          );
        });
        if (!children?.length) return null as never;
        return {
          ...item,
          children,
        };
      }
      if (!item.permissions) return item;
      if (item?.useSomePermission) {
        return item?.permissions?.some((permission) =>
          canAccess(permission as never)
        )
          ? item
          : null;
      }
      return item?.permissions?.every((permission) =>
        canAccess(permission as never)
      )
        ? item
        : null;
    })
    .filter(Boolean);

  return filterList as SidebarMenuRoute[];
};

export const getSidebarBottomMenuRoutes = (
  t: i18n["t"],
  canAccess: ReturnType<typeof usePermission>["canAccess"]
): SidebarMenuRoute[] => {
  const list = [
    {
      id: "productActivity",
      title: t("productivityManangement"),
      icon: PackageIcon,
      permissions: [
        "PAMachineGroupList",
        "PAMachineList",
        "PAReasonStopList",
        "PAProductList",
        "PAStandardQuantityList",
        "PATemplateList",
        "PARunSalary",
        "PASalary",
        "PAProductionLogList",
        "PAMachineDowntimeList",
        "PAOTWithoutMachineOperationsList",
      ],
      useSomePermission: true,
      children: [
        {
          id: "PAProduct",
          title: t("configPayrollTemplate"),
          icon: Settings01Icon,
          path: Paths.PATemplate,
          permissions: ["PATemplateList"],
        },
        {
          id: "PARunProductivity",
          title: t("runPayroll"),
          icon: Settings01Icon,
          path: Paths.PARunProductivity,
          permissions: ["PARunSalary"],
        },
        {
          id: "PAProductivity",
          title: t("payrollSheet"),
          icon: Settings01Icon,
          path: Paths.PAProductivitySalary,
          permissions: ["PASalary"],
        },
        {
          id: "PAProductivitySummary",
          title: t("payrollProductivitySummary"),
          icon: Settings01Icon,
          path: Paths.PAProductivitySalarySummary,
          permissions: ["PASalarySummary"],
        },
        {
          id: "PAOTData",
          title: t("productivityData"),
          icon: Settings01Icon,
          permissions: [
            "PAProductionLogList",
            "PAMachineDowntimeList",
            "PAOTWithoutMachineOperationsList",
          ],
          useSomePermission: true,
          children: [
            {
              id: "PAProductionLog",
              title: t("productionLogs"),
              icon: Settings01Icon,
              path: Paths.PAProductionLogs,
              permissions: ["PAProductionLogList"],
            },
            {
              id: "PAMachineDowntime",
              title: t("machineDowntime"),
              icon: Settings01Icon,
              path: Paths.PAMachineDowntime,
              permissions: ["PAMachineDowntimeList"],
            },
            {
              id: "PAOTWithoutMachineOperation",
              title: t("otWithoutMachineOperation"),
              icon: Settings01Icon,
              path: Paths.PAOTWithoutMachineOperations,
              permissions: ["PAOTWithoutMachineOperationsList"],
            },
          ],
        },
        {
          id: "paMasterData",
          title: t("masterData"),
          icon: Settings01Icon,
          useSomePermission: true,
          permissions: [
            "PAMachineList",
            "PAMachineGroupList",
            "PAReasonStopList",
            "PAProductList",
            "PAStandardQuantityList",
          ],
          children: [
            // {
            //   id: "machineGroup",
            //   title: t("machineGroup"),
            //   icon: Settings01Icon,
            //   path: Paths.PAMachineGroup,
            //   permissions: ["PAMachineGroupList"],
            // },
            // {
            //   id: "machine",
            //   title: t("machine"),
            //   icon: Settings01Icon,
            //   path: Paths.PAMachine,
            //   permissions: ["PAMachineList"],
            // },
            {
              id: "reasonStop",
              title: t("reasonStopMachine"),
              icon: Settings01Icon,
              path: Paths.PAReasonStop,
              permissions: ["PAReasonStopList"],
            },
            // {
            //   id: "product",
            //   title: t("product"),
            //   icon: Settings01Icon,
            //   path: Paths.PAProduct,
            //   permissions: ["PAProductList"],
            // },
            {
              id: "StandardQuantity",
              title: t("standardQuantity"),
              icon: Settings01Icon,
              path: Paths.PAStandardQuantity,
              permissions: ["PAStandardQuantityList"],
            },
          ],
        },
      ],
    },
    {
      id: "MasterData",
      title: t("masterData"),
      icon: Settings01Icon,
      permissions: [],
      children: [
        {
          id: "currency",
          title: t("currency"),
          icon: Settings01Icon,
          path: Paths.Currency,
          permissions: ["MasterDataCurrencyList"],
        },
        {
          id: "ethnicity",
          title: t("ethnicity"),
          icon: Settings01Icon,
          path: Paths.Ethnicity,
          permissions: ["MasterDataEthnicityList"],
        },
        {
          id: "country",
          title: t("country"),
          icon: Settings01Icon,
          path: Paths.Country,
          permissions: ["MasterDataCountryList"],
        },
        {
          id: "province",
          title: t("province"),
          icon: Settings01Icon,
          path: Paths.Province,
          permissions: ["MasterDataProvinceList"],
        },
        {
          id: "district",
          title: t("district"),
          icon: Settings01Icon,
          path: Paths.District,
          permissions: ["MasterDataDistrictList"],
        },
        {
          id: "ward",
          title: t("ward"),
          icon: Settings01Icon,
          path: Paths.Ward,
          permissions: ["MasterDataWardList"],
        },
        {
          id: "timezones",
          title: t("timezone"),
          icon: Settings01Icon,
          path: Paths.Timezone,
          permissions: ["MasterDataTimezoneList"],
        },
        {
          id: "bank",
          title: t("bank"),
          icon: Settings01Icon,
          path: Paths.Bank,
          permissions: ["MasterDataBankList"],
        },
        {
          id: "pit-rate",
          title: t("sideBarPItRate"),
          icon: Settings01Icon,
          path: Paths.PITRate,
          permissions: ["PayrollSettingPITRateList"],
        },
        {
          id: "regional-minimum-wage",
          title: t("sideBarRegionalMinimumWage"),
          icon: Settings01Icon,
          path: Paths.RegionalMinimumWage,
          permissions: ["PayrollSettingRegionalMinimumWageList"],
        },
      ],
    },
    {
      id: "auditLog",
      title: t("auditLog"),
      icon: File02Icon,
      path: Paths.AuditLog,
      permissions: ["AuditLogList"],
    },
    {
      id: "payroll-settings",
      title: t("sideBarPayrollSettings"),
      icon: Settings03Icon,
      children: [
        {
          id: "holiday",
          title: t("sideBarHoliday"),
          icon: Settings03Icon,
          path: Paths.Holiday,
          permissions: ["PayrollSettingHolidayList"],
        },
        {
          id: "leave-type",
          title: t("sideBarLeaveType"),
          icon: Settings03Icon,
          path: Paths.LeaveType,
          permissions: ["PayrollSettingLeaveTypeList"],
        },
        {
          id: "allowance",
          title: t("sideBarAllowance"),
          icon: Settings03Icon,
          path: Paths.Allowance,
          permissions: ["PayrollSettingAllowanceList"],
        },
        {
          id: "work-shift",
          title: t("sideBarWorkShift"),
          icon: Settings03Icon,
          path: Paths.WorkShift,
          permissions: ["PayrollSettingWorkShiftList"],
        },
        {
          id: "compensation",
          title: t("sideBarCompensationPackage"),
          icon: Settings03Icon,
          path: Paths.Compensation,
          permissions: ["PayrollSettingCompensationPackageList"],
        },
        {
          id: "payroll-template",
          title: t("sideBarPayrollTemplate"),
          icon: Settings03Icon,
          path: Paths.PayrollTemplate,
          permissions: ["PayrollSettingPayrollTemplateList"],
        },
        {
          id: "earning",
          title: t("sideBarEarning"),
          icon: Settings03Icon,
          path: Paths.Earning,
          permissions: ["PayrollSettingEarningList"],
        },
        {
          id: "benefitsInKind",
          title: t("benefitInKind"),
          icon: Settings03Icon,
          path: Paths.BenefitsInKind,
          permissions: ["PayrollSettingBenefitsInKindList"],
        },
        {
          id: "ot-rate",
          title: t("sideBarOTRate"),
          icon: Settings03Icon,
          path: Paths.OTRate,
          permissions: ["PayrollSettingOTRateList"],
        },
        {
          id: "deduction",
          title: t("sideBarDeduction"),
          icon: Settings01Icon,
          path: Paths.Deduction,
          permissions: ["PayrollSettingDeductionList"],
        },
        {
          id: "approval-workflow",
          title: t("approvalDefinition"),
          icon: Settings03Icon,
          path: Paths.ApprovalWorkflow,
          permissions: ["PayrollSettingApprovalWorkflowList"],
        },
        {
          id: "email-template",
          title: t("emailTemplate"),
          icon: Mail01Icon,
          path: Paths.EmailTemplate,
        },
        {
          id: "payslip-template",
          title: t("payslipTemplate"),
          icon: File02Icon,
          path: Paths.PayslipTemplate,
        },
      ],
    },
  ];
  const filterList = list
    .map((item) => {
      if (Array.isArray(item.children) && item.children?.length) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children = (item?.children as any[])?.filter((child) => {
          if (!child.permissions?.length) return true;
          return child?.useSomePermission
            ? Array.isArray(child?.permissions) &&
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                child?.permissions?.some((permission: string) =>
                  canAccess(permission as never)
                )
            : !!(
                Array.isArray(child?.permissions) &&
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                child?.permissions?.every((permission: string) =>
                  canAccess(permission as never)
                )
              );
        });
        if (!children?.length) return null as never;
        return {
          ...item,
          children,
        };
      }
      if (!item.permissions) return item;
      return item?.permissions?.every((permission) =>
        canAccess(permission as never)
      )
        ? item
        : null;
    })
    .filter(Boolean);
  return filterList as SidebarMenuRoute[];
};
